import React, { useCallback, useState } from 'react';
import {
  Button,
  Divider,
  FormControlLabel,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Delete, DeleteForever, Replay, Save,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { InfoModal, Title } from 'shared/components/abstract/interactables';
import { adminNasEntity, nasEntity } from 'shared/entityDetails';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { TextEditor, TextEditorWithHelpIcon } from 'shared/components/data-display/editors';
import { useNotification, useRequest } from 'shared/hooks';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import CancelConfirm from 'shared/components/dialog/CancelConfirm';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import StyledSwitch from 'shared/components/styled/switch';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';
import * as routes from 'shared/routes/networking';
import { getBuildingToolsPath } from 'shared/routes/networking';
import useAdminNAS from 'features/networking/core/hooks';
import { AdminNasOption, adminNasReducerActions } from 'features/networking/core/reducer';
import { isValidIp } from 'features/networking/core/helper';

const nasSecretHelpText = (
  <Stack>
    <Typography>
      If no password is provided, a secure server password will be used.
      User provided passwords must conform to the following:
    </Typography>
    <List>
      <ListItem>
        • At least 32 characters consisting of upper and lower case letters
      </ListItem>
      <ListItem>• Admin and Tenant Secrets must be different if both provided</ListItem>
    </List>
  </Stack>
);

function validateSecret(secret, name) {
  if (typeof secret !== 'string') {
    throw new Error(`${name} must be a string`);
  }
  if (!secret) {
    return `${name} is required`;
  }
  if (secret.length < 32) {
    return `${name} must be at least 32 characters`;
  }
  const hasUpperCase = /[A-Z]/.test(secret);
  if (!hasUpperCase) {
    return `${name} must contain at least one uppercase letter`;
  }

  const hasLowerCase = /[a-z]/.test(secret);
  if (!hasLowerCase) {
    return `${name} must contain at least one lowercase letter`;
  }

  return null;
}

const adminNasValidations = (pushError) => ({
  onCreate: (body) => {
    if (!isValidIp(body.nas_name)) {
      pushError('Nas IP must be a valid IPv4 address');
      return false;
    }
    if (!body?.short_name) {
      pushError('Short name is required');
      return false;
    }

    if (Object.prototype.hasOwnProperty.call(body, 'secret')) {
      const error = validateSecret(body.secret, 'Admin Secret');
      if (error) {
        pushError(error);
        return false;
      }
    }
    if (Object.prototype.hasOwnProperty.call(body, 'tenant_secret')) {
      const error = validateSecret(body.tenant_secret, 'Tenant Secret');
      if (error) {
        pushError(error);
        return false;
      }
    }

    if (body?.secret && body?.tenant_secret && body.secret === body.tenant_secret) {
      pushError('Admin and Tenant secrets must be different');
      return false;
    }
    return true;
  },
  onUpdate: (body) => {
    if (Object.prototype.hasOwnProperty.call(body, 'secret')) {
      const error = validateSecret(body.secret, 'Admin Secret');
      if (error) {
        pushError(error);
        return false;
      }
    }
    if (Object.prototype.hasOwnProperty.call(body, 'tenant_secret')) {
      const error = validateSecret(body.tenant_secret, 'Tenant Secret');
      if (error) {
        pushError(error);
        return false;
      }
    }
    return true;
  },
});

const getNasIDParam = (params) => (params.has('nas_id') ? params.get('nas_id') : null);

const tenantNasSelect = withAsyncSelect({
  ...nasEntity,
  label: 'Main Router',
  placeholder: 'Search Tenant Authentication Nas...',
});

function deleteIcon(row, onDelete, disabled) {
  return (
    <InfoModal
      disabled={disabled}
      disabledTip="Must first remove VTR from any associated buildings"
      tip="Delete VTR"
      icon={disabled ? DeleteForever : Delete}
      className={disabled ? 'disable' : 'trash'}
      message="Delete VTR"
      title={row.ip}
      actionName="Remove"
      color={BUTTON_COLORS.error}
      description={<p>{`This will delete VTR ${row.ip}. Are you sure?`}</p>}
      actionCallback={() => (onDelete(row.admin_nas.id))}
    />
  );
}

function linkToBuildingTools(np) {
  return (
    np.buildings_dot1x.length > 0
      ? (
        <Link
          href={getBuildingToolsPath(
            np.buildings_dot1x.map((building) => building.building_id),
          )}
          underline="hover"
          target="_blank"
        >
          {np.buildings_dot1x.length}
        </Link>
      ) : 0
  );
}

function linkToVTR(np) {
  return (
    <Link
      href={`${routes.equipmentPath}?nas_id=${np.admin_nas.id}`}
      underline="hover"
      target="_blank"
    >
      {np.ip}
    </Link>
  );
}

function linkToBuildingCard(building) {
  return (
    <Link
      href={getBuildingToolsPath([building.id])}
      underline="hover"
      target="_blank"
    >
      {building.name}
    </Link>
  );
}

export default function EquipmentManagement() {
  const {
    search,
    pathname,
  } = useLocation();
  const { push } = useHistory();
  const { pushError } = useNotification();

  const params = new URLSearchParams(search);
  const nasID = getNasIDParam(params);
  const validate = adminNasValidations(pushError);
  const { request } = useRequest();

  const {
    data: adminNAS,
    updatedData,
    dirty,
    dispatch,
    get: getNas,
    update: updateNas,
    create: createNas,
    deleteNas,
  } = useAdminNAS(nasID);
  const creating = adminNAS?.id === undefined; // true if NAS is being created
  const creatable = creating
    && updatedData.short_name
    && (adminNAS.option === AdminNasOption.vtr ? updatedData.vtr_nas_id : true);

  const [deleting, setDeleting] = useState(false);

  const nasDescriptions = [
    {
      key: 'nas_name',
      label: 'Nas IP',
      editor: TextEditor,
      readOnly: true,
      required: true,
    },
    {
      key: 'short_name',
      label: 'Shortname',
      editor: TextEditor,
      readOnly: !creating,
      required: true,
    },
    {
      key: 'secret',
      label: 'Admin Secret',
      editor: TextEditorWithHelpIcon,
      required: !creating,
      tooltipText: nasSecretHelpText,
      fullWidth: true,
      onFocus: () => {
        if (updatedData.secret || creating) return;
        dispatch({
          action: adminNasReducerActions.update,
          payload: {
            key: 'secret',
            value: '',
          },
        });
      },
      onBlur: () => {
        if (dirty && updatedData.secret) return;
        dispatch({
          action: adminNasReducerActions.update,
          payload: {
            key: 'secret',
            value: adminNAS?.secret,
          },
        });
      },
    },
  ];

  const tenantNasDescriptions = [
    {
      key: 'tenant_secret',
      label: 'Tenant Auth Secret',
      editor: TextEditorWithHelpIcon,
      readOnly: adminNAS?.option !== AdminNasOption.tenant,
      required: !creating,
      tooltipText: nasSecretHelpText,
      formatter: (d) => (d?.tenant_secret ? d?.tenant_secret : ' '),
      onFocus: () => {
        if (updatedData.tenant_secret || creating) return;
        dispatch({
          action: adminNasReducerActions.update,
          payload: {
            key: 'tenant_secret',
            value: '',
          },
        });
      },
      onBlur: () => {
        if (dirty && updatedData.tenant_secret) return;
        dispatch({
          action: adminNasReducerActions.update,
          payload: {
            key: 'tenant_secret',
            value: adminNAS?.tenant_secret,
          },
        });
      },
    },
  ];

  const nasProfileDescriptions = [
    {
      key: 'vtr_nas_id',
      label: 'Main Router',
      readOnly: !creating || adminNAS?.option !== AdminNasOption.vtr,
      editor: tenantNasSelect,
      formatter: (d) => (d?.nas_profile ? `${d?.nas_profile?.nas?.nas_name} (${d?.nas_profile?.nas?.description})` : ' '),
    },
  ];

  const handleUpdate = useCallback(() => {
    const body = { ...adminNAS, ...updatedData };

    if (!Object.prototype.hasOwnProperty.call(updatedData, 'secret')) delete body.secret;
    if (!Object.prototype.hasOwnProperty.call(updatedData, 'tenant_secret')) delete body.tenant_secret;

    if (!validate.onUpdate(body)) {
      return;
    }

    updateNas(body);
  }, [adminNAS, updateNas, updatedData, validate]);

  const handleDelete = async () => {
    const deleted = await deleteNas();
    setDeleting(false);
    // A failed delete keeps the NAS on screen, so it's clear it still exists.
    if (deleted) {
      push({
        pathname,
        search: '',
      });
    }
  };

  const handleCreate = useCallback(async () => {
    const body = {
      ...adminNAS,
      ...updatedData,
    };
    if (!validate.onCreate(body, pushError)) {
      return;
    }

    const newNas = await createNas(body);
    if (!newNas) return;

    push({
      pathname,
      search: `${adminNasEntity.queryKey}=${newNas?.id}`,
    });
  }, [adminNAS, createNas, pathname, push, pushError, updatedData, validate]);

  const handleSelectEquipment = (option) => {
    if (option?.staged) {
      dispatch({
        action: adminNasReducerActions.set,
        payload: { nas_name: option.name },
      });
    } else if (option === null) {
      dispatch({
        action: adminNasReducerActions.set,
        payload: undefined,
      });
    }

    push({
      pathname,
      search: option?.id === undefined
        ? ''
        : `${adminNasEntity.queryKey}=${option.id}`,
    });
  };

  const handleChangeData = (key, value) => {
    dispatch({
      action: adminNasReducerActions.update,
      payload: {
        key,
        value,
      },
    });
  };

  const existingVTRBuildings = adminNAS?.nas_profile?.buildings_dot1x
    ?.map((dot1x) => dot1x.building);

  const buildingColumns = [
    {
      name: 'Name',
      formatter: (row) => (linkToBuildingCard(row)),
      orderBy: 'name',
    },
    {
      name: 'Address',
      formatter: (row) => (`${row.address_1} ${row.address_2}`),
    },
    {
      name: 'Auth Method',
      jsonKey: 'auth_method',
      orderBy: 'ip_internet_auth_method',
    },
  ];

  const existingVTRs = adminNAS?.tenant_nas?.nas_profiles?.filter(
    (nasProfile) => nasProfile.ip?.length > 0,
  );

  const vtrsContainBuildings = (existingVTRs?.filter(
    (vtr) => vtr.buildings_dot1x.length > 0,
  )
  )?.length > 0;

  const deleteVTR = async (id) => {
    const result = await request(adminNasEntity.deleteRequest(id), 'Deleted VTR');
    if (result) {
      await getNas();
    }
  };

  const deleteAllVTR = async (nasProfiles) => {
    const result = await Promise.all(nasProfiles.map((np) => request(adminNasEntity.deleteRequest(np.admin_nas.id), 'Deleted VTR')));
    if (result) {
      await getNas();
    }
  };

  const VTRcolumns = [
    {
      name: 'IP',
      formatter: (np) => linkToVTR(np),
    },
    {
      name: 'Buildings',
      formatter: (np) => linkToBuildingTools(np),
    },

    {
      name: 'Delete',
      formatter: (np) => deleteIcon(
        np,
        deleteVTR,
        np.buildings_dot1x.length > 0,
      ),
    },
  ];

  return (
    <Grid container spacing={1}>
      <Title
        title="Add Equipment"
        titleLink={routes.equipmentPath}
      />
      <Grid size={{ xs: 12, lg: 5, xl: 3 }}>
        <AsyncSearchableSelect
          searchRequest={adminNasEntity.searchRequest}
          baseSearch={adminNasEntity.baseSearch}
          getOptionLabel={adminNasEntity.getOptionLabel}
          getOptionValue={adminNasEntity.getOptionValue}
          onChange={handleSelectEquipment}
          selectedOption={adminNAS}
          label={adminNasEntity.label}
          creatable
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {adminNAS && (
        <Grid container spacing={1} sx={{ justifyContent: 'space-between' }}>
          <Grid
            size={{ xs: 12, lg: 6 }}
            container
          >
            <GridWrapper title="Details" size={12}>
              <DataFormatter
                data={adminNAS}
                descriptions={nasDescriptions}
                updatedData={updatedData}
                editing
                updateValue={handleChangeData}
              />
              <GridWrapper title="Optional" alignItems="center" size={12}>
                <Grid size={12} container>
                  <Grid>
                    <FormControlLabel
                      label="Authenticate Tenants"
                      control={(
                        <StyledSwitch
                          checked={adminNAS.option === AdminNasOption.tenant}
                          disabled={!creating}
                          onChange={(event) => {
                            dispatch({
                              action: adminNasReducerActions.toggleAuthTenant,
                              payload: { checked: event.target.checked },
                            });
                          }}
                          slotProps={{ input: { 'aria-label': 'controlled' } }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid>
                    <FormControlLabel
                      label="VTR"
                      control={(
                        <StyledSwitch
                          checked={adminNAS.option === AdminNasOption.vtr}
                          disabled={!creating}
                          onChange={(event) => {
                            dispatch({
                              action: adminNasReducerActions.toggleIsVTR,
                              payload: { checked: event.target.checked },
                            });
                          }}
                          slotProps={{ input: { 'aria-label': 'controlled' } }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <DataFormatter
                    data={adminNAS}
                    descriptions={tenantNasDescriptions}
                    updatedData={updatedData}
                    updateValue={handleChangeData}
                    editing
                  />
                </Grid>
                <Grid size={12}>
                  <DataFormatter
                    data={adminNAS}
                    descriptions={nasProfileDescriptions}
                    updatedData={updatedData}
                    updateValue={handleChangeData}
                    editing
                  />
                </Grid>
              </GridWrapper>
            </GridWrapper>
            <Grid container size={12} sx={{ justifyContent: 'end' }}>
              {creating && (
                <Grid>
                  <Button
                    variant={VARIANTS.contained}
                    onClick={handleCreate}
                    disabled={!creatable}
                  >
                    Create
                  </Button>
                </Grid>
              )}
              {dirty && !creating && (
                <Grid container>
                  <Grid>
                    <Button
                      startIcon={<Replay />}
                      onClick={() => dispatch({ action: adminNasReducerActions.clear })}
                      variant={VARIANTS.outlined}
                    >
                      Clear
                    </Button>
                  </Grid>
                  <Grid>
                    <Button
                      variant={VARIANTS.contained}
                      startIcon={<Save />}
                      onClick={handleUpdate}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              )}
              {!creating && !dirty && (
                <Grid>
                  <Tooltip
                    title={existingVTRs?.length > 0 && 'Nas has VTRs associated with it that must first be removed'}
                  >
                    <span>
                      <Button
                        color={BUTTON_COLORS.error}
                        variant={VARIANTS.contained}
                        startIcon={<Delete />}
                        onClick={() => setDeleting(true)}
                        disabled={existingVTRs?.length > 0}
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid
            size={{ xs: 12, lg: 5 }}
            container
          >
            {existingVTRs && (
              <GridWrapper title="Associated VTRs" size={12}>
                <Grid container spacing={1} sx={{ justifyContent: 'end' }}>
                  <Grid size={12}>
                    <StickyHeadTable
                      columns={VTRcolumns}
                      noRowsText="No Associated VTRs"
                      rowId="id"
                      rows={existingVTRs}
                      striped
                    />
                  </Grid>
                  {existingVTRs?.length > 1 && (
                    <Grid>
                      <InfoModal
                        color={BUTTON_COLORS.error}
                        title="Delete all associated VTRs"
                        actionName="Delete All VTRs"
                        description={`This will delete ${existingVTRs?.length} VTRs and all building associations affiliated with Main Router '${adminNAS.tenant_nas?.nas_name}'. Are you sure?`}
                        actionCallback={() => (deleteAllVTR(existingVTRs))}
                        disabled={existingVTRs?.length === 0
                          || !existingVTRs || vtrsContainBuildings}
                        disabledTip="VTRs have building associations that must first be removed"
                      />
                    </Grid>
                  )}
                </Grid>
              </GridWrapper>
            )}
            {existingVTRBuildings && (
              <GridWrapper title="Associated Buildings" size={12}>
                <StickyHeadTable
                  columns={buildingColumns}
                  noRowsText="No Associated Buildings"
                  rowId="id"
                  rows={existingVTRBuildings}
                  striped
                />
              </GridWrapper>
            )}
          </Grid>
        </Grid>
      )}
      <Grid>
        <CancelConfirm
          open={deleting}
          title={existingVTRBuildings?.length > 0 ? 'Confirm Nas and VTR deletion' : 'Confirm NAS deletion'}
          text={existingVTRBuildings?.length > 0 ? `This will delete the Nas and VTR "${adminNAS.nas_name}", as well as all associated building zone information. Are you sure?` : 'Are you sure want to delete the selected equipment?'}
          onCancel={() => setDeleting(false)}
          onConfirm={handleDelete}
          cancelText="cancel"
          confirmText="delete"
        />
      </Grid>
    </Grid>
  );
}
